package com.htmlagent.site.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.theme.TooltipBottomEndStyle
import com.htmlagent.site.theme.TooltipBottomStyle
import com.htmlagent.site.theme.TooltipBubbleStyle
import com.htmlagent.site.theme.TooltipHostStyle
import com.htmlagent.site.theme.TooltipRightStyle
import com.htmlagent.site.theme.TooltipTopEndStyle
import com.htmlagent.site.theme.TooltipTopStyle
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import org.w3c.dom.pointerevents.PointerEvent

/**
 * A themed tooltip around a control that is nothing but an icon.
 *
 * The native tooltip takes a second and cannot be styled; wrapped controls use `named` instead
 * of `labelled` so they do not end up with two tooltips. It also shows on focus, but only on
 * keyboard focus: the browser is asked through `:focus-visible`, because a click focuses too and
 * would leave it stuck. The state lives in Kotlin and not in CSS because a descendant selector
 * would have to target the class Silk generates. It carries `aria-hidden`: the control already
 * has the same text in its `aria-label`.
 *
 * Hover is a real mouse only. A tap on a phone fires a compatibility `mouseenter` and no
 * `mouseleave` until the next tap elsewhere, so the tooltip opened on every press and stayed.
 */
@Composable
fun Tooltip(
    text: String,
    side: TipSide = TipSide.Right,
    content: @Composable () -> Unit,
) {
    var visible by remember { mutableStateOf(false) }

    Box(
        TooltipHostStyle.toModifier()
            .attrsModifier {
                ref { element ->
                    val onEnter: (Event) -> Unit = { event ->
                        if ((event as? PointerEvent)?.pointerType == "mouse") visible = true
                    }
                    val onLeave: (Event) -> Unit = { visible = false }
                    element.addEventListener("pointerenter", onEnter)
                    element.addEventListener("pointerleave", onLeave)
                    onDispose {
                        element.removeEventListener("pointerenter", onEnter)
                        element.removeEventListener("pointerleave", onLeave)
                    }
                }
                // Focus lands on a descendant: `focusin`/`focusout` bubble, `focus`/`blur` do not.
                onFocusIn { event ->
                    val target = event.target as? Element
                    if (target != null && target.matches(":focus-visible")) visible = true
                }
                onFocusOut { visible = false }
                // Escape dismisses it without moving focus, in case it covers something to read.
                onKeyDown { event -> if (event.key == "Escape") visible = false }
            },
    ) {
        content()

        if (visible) {
            SpanText(
                text,
                TooltipBubbleStyle.toModifier()
                    .then(sideStyle(side))
                    .attrsModifier { attr("aria-hidden", "true") },
            )
        }
    }
}

@Composable
private fun sideStyle(side: TipSide): Modifier = when (side) {
    TipSide.Right -> TooltipRightStyle.toModifier()
    TipSide.Bottom -> TooltipBottomStyle.toModifier()
    TipSide.BottomEnd -> TooltipBottomEndStyle.toModifier()
    TipSide.TopEnd -> TooltipTopEndStyle.toModifier()
    TipSide.Top -> TooltipTopStyle.toModifier()
}

/** Which way the tooltip comes out. Decided by whoever places it, by where there is room. */
enum class TipSide { Right, Bottom, BottomEnd, TopEnd, Top }
